import { cva, type VariantProps } from 'class-variance-authority';
import Image from 'next/image';
import Link from 'next/link';
import { Slot as SlotPrimitive } from 'radix-ui';
import type * as React from 'react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getEnergy } from '@/utils/helpers';
const DEFAULT_BADGE_COLOR = '#ff88e2';
const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;
const isValidHexColor = (color: string | null | undefined): color is string => !!color && HEX_COLOR_REGEX.test(color);
const tileVariants = cva('group relative -mx-3 flex flex-col rounded-lg p-3 transition-colors focus:outline-hidden', {
  variants: {
    size: {
      default: 'w-auto',
      fixed: 'w-48'
    },
    hover: {
      true: 'can-hover:hover:bg-surface-hover',
      false: ''
    }
  },
  defaultVariants: {
    size: 'default',
    hover: true
  }
});
export interface TileProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>, VariantProps<typeof tileVariants> {
  asChild?: boolean;
  href?: string;
  ref?: React.Ref<HTMLAnchorElement>;
}
function Tile({
  className,
  size,
  hover,
  asChild = false,
  href,
  ref,
  children,
  ...props
}: TileProps) {
  const tileClass = cn(tileVariants({
    size,
    hover,
    className
  }));
  if (asChild) {
    return <SlotPrimitive.Slot className={tileClass} ref={ref} {...props}>
        {children}
      </SlotPrimitive.Slot>;
  }
  if (!href) {
    throw new Error('Tile component requires an href prop when not using asChild');
  }

  // The inner wrapper is image-sized — the hover-card padding makes the
  // anchor wider than the image, so absolute children (overlay banners,
  // badges) need a positioning context that matches the image, not the
  // padded hover area.
  return <Link className={tileClass} href={href} ref={ref} {...props} data-sentry-element="Link" data-sentry-component="Tile" data-sentry-source-file="tile.tsx">
      <span className="relative flex h-full flex-col">{children}</span>
    </Link>;
}
Tile.displayName = 'Tile';
const tileImageVariants = cva('relative overflow-hidden transition', {
  variants: {
    variant: {
      default: 'aspect-square w-full rounded-lg',
      tall: 'w-full rounded-lg',
      wide: 'w-full rounded-lg',
      video: 'aspect-video w-full rounded-xl',
      'featured-banner': 'h-full w-full',
      artist: 'aspect-square w-full rounded-full'
    },
    aspectRatio: {
      square: 'aspect-square',
      tall: 'aspect-[1/1.4]',
      wide: 'aspect-[2/1]'
    }
  },
  defaultVariants: {
    variant: 'default',
    aspectRatio: 'square'
  }
});
interface TileImageProps extends React.HTMLAttributes<HTMLDivElement> {
  alt: string;
  aspectRatio?: 'square' | 'tall' | 'wide';
  fill?: boolean;
  imgClassName?: string;
  loading?: 'lazy' | 'eager';
  ref?: React.Ref<HTMLDivElement>;
  sizes?: string;
  src: string;
  variant?: 'default' | 'tall' | 'wide' | 'video' | 'featured-banner' | 'artist';
}
function TileImage({
  className,
  fill = true,
  variant = 'default',
  aspectRatio,
  imgClassName,
  loading,
  sizes,
  src,
  alt,
  ref,
  children,
  ...divProps
}: TileImageProps) {
  return <div className={cn(tileImageVariants({
    variant,
    aspectRatio
  }), 'group-focus-visible:ring-2 group-focus-visible:ring-pink-500 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-white dark:group-focus-visible:ring-offset-gray-950', className)} ref={ref} {...divProps} data-sentry-component="TileImage" data-sentry-source-file="tile.tsx">
      <Image alt={alt} className={cn('object-cover', {
      'h-full': variant === 'featured-banner'
    }, imgClassName)} fill={fill} loading={loading} sizes={sizes ?? ['(max-width: 767px) 128px', '(max-width: 1600px) 256px', '384px'].join(', ')} src={src} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
      {children}
    </div>;
}
TileImage.displayName = 'TileImage';
interface TileContentProps extends React.HTMLAttributes<HTMLDivElement> {
  mode?: 'full' | 'title-only' | 'description-only';
  ref?: React.Ref<HTMLDivElement>;
  visible?: boolean;
}
function TileContent({
  className,
  visible = false,
  mode = 'full',
  ref,
  ...props
}: TileContentProps) {
  return <div className={cn(visible ? 'grow overflow-hidden p-0' : 'sr-only', className)} data-content-mode={mode} ref={ref} {...props} data-sentry-component="TileContent" data-sentry-source-file="tile.tsx" />;
}
TileContent.displayName = 'TileContent';
interface TileTextProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  ref?: React.Ref<HTMLHeadingElement>;
}
function TileTitle({
  className,
  asChild = false,
  children,
  ref,
  ...props
}: TileTextProps) {
  const Comp = asChild ? SlotPrimitive.Slot : 'h3';
  return <Comp className={cn('mt-2 mb-0 truncate font-sans font-semibold text-gray-950 text-sm group-focus-visible:underline group-focus-visible:underline-offset-1 md:text-base dark:text-white', className)} ref={ref} {...props} data-sentry-element="Comp" data-sentry-component="TileTitle" data-sentry-source-file="tile.tsx">
      {children}
    </Comp>;
}
TileTitle.displayName = 'TileTitle';
interface TileDescriptionProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  ref?: React.Ref<HTMLParagraphElement>;
}
function TileDescription({
  className,
  asChild = false,
  ref,
  ...props
}: TileDescriptionProps) {
  const Comp = asChild ? SlotPrimitive.Slot : 'p';
  return <Comp className={cn('line-clamp-2 whitespace-normal text-gray-700 text-sm leading-5 dark:text-gray-200', className)} ref={ref} {...props} data-sentry-element="Comp" data-sentry-component="TileDescription" data-sentry-source-file="tile.tsx" />;
}
TileDescription.displayName = 'TileDescription';
interface TileSimilarProps extends React.HTMLAttributes<HTMLDivElement> {
  alt: string;
  artistName: string;
  energy: number;
  leftImage?: string;
  mainImage: string;
  ref?: React.Ref<HTMLDivElement>;
  rightImage?: string;
  trackId: number;
  trackName: string;
}
function TileSimilar({
  className,
  mainImage,
  leftImage,
  rightImage,
  trackName,
  artistName,
  energy,
  trackId,
  alt,
  ref,
  ...divProps
}: TileSimilarProps) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const backgroundGradientImage = getEnergy(energy, trackId);
  if (!mainImage) {
    return null;
  }

  // Use main image as fallback for missing side images
  const finalLeftImage = leftImage || mainImage;
  const finalRightImage = rightImage || finalLeftImage;
  return <div className={cn('relative aspect-square w-full overflow-hidden rounded-lg transition group-focus-visible:ring-2 group-focus-visible:ring-pink-500 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-white dark:group-focus-visible:ring-offset-gray-950', className)} ref={ref} style={{
    backgroundImage: `url(${backgroundGradientImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  } as React.CSSProperties} {...divProps} data-sentry-component="TileSimilar" data-sentry-source-file="tile.tsx">
      {/* Main Avatar */}
      <div className="absolute top-[33%] left-1/2 z-10 h-[46%] w-[46%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white shadow-[0_0_0_2px_rgba(255,255,255,1)] sm:shadow-[0_0_0_3px_rgba(255,255,255,1)]">
        <Image alt={alt} className={cn('object-cover transition-opacity', hasLoaded ? 'opacity-100' : 'opacity-0')} fill onLoad={() => setHasLoaded(true)} sizes="(max-width: 767px) 60px, (max-width: 991px) 115px, (max-width: 1100px) 95px, (max-width: 1600px) 85px, 120px" src={mainImage} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
      </div>

      {/* Left Avatar */}
      <div className="absolute top-[48%] left-[26%] z-5 h-[30%] w-[30%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white shadow-[0_0_0_2px_rgba(255,255,255,1)] sm:h-[35%] sm:w-[35%] sm:shadow-[0_0_0_3px_rgba(255,255,255,1)]">
        <Image alt={alt} className="object-cover" fill sizes="(max-width: 767px) 45px, (max-width: 991px) 86px, (max-width: 1100px) 71px, (max-width: 1600px) 64px, 90px" src={finalLeftImage} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
      </div>

      {/* Right Avatar */}
      <div className="absolute top-[48%] right-[26%] z-5 h-[30%] w-[30%] translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white shadow-[0_0_0_2px_rgba(255,255,255,1)] sm:h-[35%] sm:w-[35%] sm:shadow-[0_0_0_3px_rgba(255,255,255,1)]">
        <Image alt={alt} className="object-cover" fill sizes="(max-width: 767px) 45px, (max-width: 991px) 86px, (max-width: 1100px) 71px, (max-width: 1600px) 64px, 90px" src={finalRightImage} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
      </div>

      {/* Track Info */}
      <div className="absolute bottom-[5.5%] left-0 w-full px-2.5 text-center text-white drop-shadow sm:bottom-[6%]">
        <strong className="fl-text-xs/base -mb-1 block truncate leading-none sm:mb-0">
          {trackName}
        </strong>
        <span className="fl-text-xs/sm block truncate leading-none">
          {artistName}
        </span>
      </div>
    </div>;
}
TileSimilar.displayName = 'TileSimilar';
interface TileCelebrationBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  badgeColor?: string | null;
  date: string;
  ref?: React.Ref<HTMLDivElement>;
}
function TileCelebrationBadge({
  className,
  date,
  badgeColor,
  ref,
  ...props
}: TileCelebrationBadgeProps) {
  const backgroundColor = isValidHexColor(badgeColor) ? badgeColor : DEFAULT_BADGE_COLOR;
  return <div className={cn('absolute right-0 bottom-[15%] px-2 py-1 font-aeonik font-semibold text-gray-950 text-sm', className)} ref={ref} style={{
    backgroundColor
  }} {...props} data-sentry-component="TileCelebrationBadge" data-sentry-source-file="tile.tsx">
      {date}
    </div>;
}
TileCelebrationBadge.displayName = 'TileCelebrationBadge';
export { Tile, TileCelebrationBadge, TileContent, TileDescription, TileImage, TileSimilar, TileTitle };